import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Calendar, Copy, Check, Eye, EyeOff, ExternalLink, Trash2, AlertTriangle, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../utils/api';

export function SettingsPage() {
  const [showSecret, setShowSecret] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [calendarSecret, setCalendarSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [promoActive, setPromoActive] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [isSavingPromo, setIsSavingPromo] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);

  // Get current domain
  const baseUrl = window.location.origin;

  useEffect(() => {
    loadSettings();
    loadActivePromo();
    loadDiscountCodes();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      if (response.success) {
        setCalendarSecret(response.settings.calendarSecret);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const loadActivePromo = async () => {
    try {
      const response = await api.getActivePromo();
      if (response.success) {
        setPromoActive(response.promo.isActive);
        setPromoCode(response.promo.code || '');
      }
    } catch (error) {
      console.error('Error loading active promo:', error);
      toast.error('Fehler beim Laden der Rabatt-Einstellungen');
    }
  };

  const loadDiscountCodes = async () => {
    try {
      const response = await api.getDiscountCodes({ status: 'active' });
      if (response.success) {
        setDiscountCodes(response.discounts);
      }
    } catch (error) {
      console.error('Error loading discount codes:', error);
    }
  };

  const handleTogglePromo = async () => {
    const nextActive = !promoActive;

    if (nextActive && !promoCode) {
      toast.error('Bitte zuerst einen Rabattcode auswählen');
      return;
    }

    setIsSavingPromo(true);
    try {
      const response = await api.updateActivePromo(nextActive, promoCode || null);
      if (response.success) {
        setPromoActive(response.promo.isActive);
        toast.success(
          nextActive
            ? `Aktiv: ${response.promo.code} wird automatisch angewendet, Streichpreise werden angezeigt`
            : 'Rabattaktion deaktiviert — kein Rabattcode und keine Streichpreise mehr'
        );
      }
    } catch (error) {
      console.error('Error updating active promo:', error);
      toast.error('Fehler beim Speichern der Einstellung');
    } finally {
      setIsSavingPromo(false);
    }
  };

  const handlePromoCodeChange = async (newCode) => {
    setPromoCode(newCode);

    // Wenn die Promo bereits aktiv ist, Code-Wechsel sofort übernehmen
    if (promoActive) {
      setIsSavingPromo(true);
      try {
        const response = await api.updateActivePromo(true, newCode);
        if (response.success) {
          toast.success(`Rabattcode aktualisiert: ${response.promo.code}`);
        }
      } catch (error) {
        console.error('Error updating active promo:', error);
        toast.error('Fehler beim Speichern der Einstellung');
      } finally {
        setIsSavingPromo(false);
      }
    }
  };

  const calendarUrls = {
    bookings: `${baseUrl}/api/admin/calendar/bookings.ics`,
    blockedDates: `${baseUrl}/api/admin/calendar/blocked-dates.ics`,
    bookingsSecret: `${baseUrl}/api/admin/calendar/${calendarSecret}/bookings.ics`,
    blockedDatesSecret: `${baseUrl}/api/admin/calendar/${calendarSecret}/blocked-dates.ics`,
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('In Zwischenablage kopiert');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDeleteAllData = async () => {
    const confirmation = window.confirm(
      '⚠️ WARNUNG: Diese Aktion löscht ALLE Buchungen und Bestellungen aus der Datenbank!\n\n' +
      'Dies kann NICHT rückgängig gemacht werden!\n\n' +
      'Möchten Sie wirklich fortfahren?'
    );

    if (!confirmation) return;

    const secondConfirmation = window.confirm(
      'Sind Sie ABSOLUT SICHER?\n\n' +
      'Letzte Chance, um abzubrechen!'
    );

    if (!secondConfirmation) return;

    setIsDeleting(true);
    try {
      const response = await api.deleteAllBookingsAndOrders();

      if (response.success) {
        toast.success(
          `Erfolgreich gelöscht: ${response.deletedBookings} Buchungen, ${response.deletedOrders} Bestellungen`
        );
      } else {
        toast.error('Fehler beim Löschen der Daten');
      }
    } catch (error) {
      console.error('Error deleting all data:', error);
      toast.error('Fehler beim Löschen der Daten: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Lade Einstellungen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Einstellungen</h1>
        <p className="mt-1 text-sm text-gray-500">
          Systemkonfiguration und Kalender-Integrationen
        </p>
      </div>

      {/* Warning if no calendar secret */}
      {!calendarSecret && (
        <Card className="p-4 bg-yellow-50 border border-yellow-200">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">⚠️ Kalender-Secret nicht konfiguriert</p>
              <p className="text-yellow-700">
                Setzen Sie die Umgebungsvariable <code className="px-2 py-0.5 bg-yellow-100 rounded font-mono text-xs">CALENDAR_SECRET</code> in Railway,
                um Secret-basierte Kalender-URLs zu aktivieren.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Promo Section: gekoppelter Rabattcode + Streichpreis-Anzeige */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Tag className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Rabattaktion</h2>
            <p className="text-sm text-gray-600 mt-1">
              Steuert den Rabattcode, der automatisch am Checkout angewendet wird, und die dazugehörige
              Streichpreis-Anzeige auf der Buchungsseite. In Shopify bleibt immer der reguläre (höhere) Preis
              hinterlegt — er wird nur als Streichpreis sichtbar, solange die Aktion aktiv ist.
            </p>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-medium text-gray-900">Rabattaktion aktiv</h3>
              <p className="text-sm text-gray-600 mt-1">
                Wenn aktiv: der ausgewählte Rabattcode wird bei jeder Buchung automatisch am Checkout angewendet
                und alle Pakete zeigen den durchgestrichenen regulären Preis. Wenn inaktiv: kein automatischer
                Rabatt, keine Streichpreise.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={promoActive}
              onClick={handleTogglePromo}
              disabled={isSavingPromo}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                promoActive ? 'bg-blue-600' : 'bg-gray-300'
              } ${isSavingPromo ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  promoActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rabattcode
            </label>
            <select
              value={promoCode}
              onChange={(e) => handlePromoCodeChange(e.target.value)}
              disabled={isSavingPromo}
              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg disabled:opacity-60"
            >
              <option value="">— Kein Code ausgewählt —</option>
              {discountCodes.map((d) => (
                <option key={d.id} value={d.code}>
                  {d.code}
                  {d.title ? ` — ${d.title}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Nur aktive Rabattcodes aus dem Bereich "Rabattcodes" werden hier gelistet.
            </p>
          </div>
        </div>
      </Card>

      {/* Calendar Integration Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Kalender-Integration</h2>
            <p className="text-sm text-gray-600 mt-1">
              Abonnieren Sie Ihre Buchungen in Google Calendar, Apple Calendar, Outlook, etc.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Bookings Calendar */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-medium text-gray-900">📸 Buchungskalender</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Alle bestätigten Fotobox-Buchungen als iCal-Feed
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Method 1: With Authentication */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700 uppercase">
                    Mit Authentifizierung (empfohlen)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={calendarUrls.bookings}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg font-mono"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCopy(calendarUrls.bookings, 'bookings')}
                    className="flex items-center gap-2"
                  >
                    {copiedField === 'bookings' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ℹ️ Beim Hinzufügen in Google Calendar werden Sie nach Benutzername und Passwort gefragt
                </p>
              </div>

              {/* Method 2: With Secret Token - Only show if secret is configured */}
              {calendarSecret && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-yellow-800 uppercase">
                      Mit Secret-Token (keine Authentifizierung)
                    </span>
                    <button
                      onClick={() => setShowSecret(!showSecret)}
                      className="text-yellow-700 hover:text-yellow-900"
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {showSecret ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={calendarUrls.bookingsSecret}
                        readOnly
                        className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg font-mono"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopy(calendarUrls.bookingsSecret, 'bookings-secret')}
                        className="flex items-center gap-2"
                      >
                        {copiedField === 'bookings-secret' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-yellow-800">Klicken Sie auf das Augen-Symbol, um die URL anzuzeigen</p>
                  )}
                  <p className="text-xs text-yellow-700 mt-2">
                    ⚠️ Diese URL enthält einen geheimen Token. Teilen Sie sie nicht öffentlich!
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Blocked Dates Calendar */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-medium text-gray-900">🚫 Sperrtage-Kalender</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Alle gesperrten Zeiträume als iCal-Feed
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Method 1: With Authentication */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700 uppercase">
                    Mit Authentifizierung (empfohlen)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={calendarUrls.blockedDates}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg font-mono"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCopy(calendarUrls.blockedDates, 'blocked')}
                    className="flex items-center gap-2"
                  >
                    {copiedField === 'blocked' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Method 2: With Secret Token - Only show if secret is configured */}
              {calendarSecret && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-yellow-800 uppercase">
                      Mit Secret-Token (keine Authentifizierung)
                    </span>
                    <button
                      onClick={() => setShowSecret(!showSecret)}
                      className="text-yellow-700 hover:text-yellow-900"
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {showSecret ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={calendarUrls.blockedDatesSecret}
                        readOnly
                        className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg font-mono"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopy(calendarUrls.blockedDatesSecret, 'blocked-secret')}
                        className="flex items-center gap-2"
                      >
                        {copiedField === 'blocked-secret' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-yellow-800">Klicken Sie auf das Augen-Symbol, um die URL anzuzeigen</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* How to Use Section */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">📱 So fügen Sie den Kalender hinzu</h3>
        <div className="space-y-4 text-sm text-gray-700">
          <div>
            <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Google Calendar
            </h4>
            <ol className="list-decimal list-inside space-y-1 pl-6">
              <li>Klicken Sie auf das "+" neben "Weitere Kalender"</li>
              <li>Wählen Sie "Per URL"</li>
              <li>Kopieren Sie eine der obigen URLs und fügen Sie sie ein</li>
              <li>Bei Authentifizierung: Geben Sie Ihren Admin-Benutzernamen und Passwort ein</li>
              <li>Klicken Sie auf "Kalender hinzufügen"</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Apple Calendar (macOS/iOS)
            </h4>
            <ol className="list-decimal list-inside space-y-1 pl-6">
              <li>Öffnen Sie die Kalender-App</li>
              <li>Gehen Sie zu "Ablage" → "Neues Kalenderabonnement"</li>
              <li>Fügen Sie die URL ein</li>
              <li>Passen Sie den Namen und die Aktualisierungsfrequenz an</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Outlook
            </h4>
            <ol className="list-decimal list-inside space-y-1 pl-6">
              <li>Gehen Sie zu "Kalender hinzufügen"</li>
              <li>Wählen Sie "Aus dem Internet"</li>
              <li>Fügen Sie die URL ein</li>
              <li>Klicken Sie auf "OK"</li>
            </ol>
          </div>
        </div>
      </Card>

      {/* Environment Variable Note */}
      <Card className="p-4 bg-gray-50 border border-gray-200">
        <p className="text-sm text-gray-700">
          <strong>Hinweis:</strong> Um den Secret-Token zu konfigurieren, setzen Sie die Umgebungsvariable{' '}
          <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">CALENDAR_SECRET</code> in Ihrer{' '}
          <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">.env</code> Datei.
        </p>
      </Card>

      {/* Danger Zone */}
      <Card className="p-6 border-2 border-red-300 bg-red-50">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <div>
            <h2 className="text-xl font-semibold text-red-900">Gefahrenzone</h2>
            <p className="text-sm text-red-700 mt-1">
              Vorsicht! Diese Aktionen können nicht rückgängig gemacht werden.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-red-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 mb-1">
                  Alle Buchungen und Bestellungen löschen
                </h3>
                <p className="text-sm text-gray-600">
                  Löscht ALLE Buchungen und Bestellungen aus der Datenbank. Dies ist nur für Test- und Entwicklungszwecke gedacht.
                  Diese Aktion kann NICHT rückgängig gemacht werden!
                </p>
              </div>
              <Button
                variant="danger"
                onClick={handleDeleteAllData}
                disabled={isDeleting}
                className="ml-4 bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
              >
                {isDeleting ? (
                  'Lösche...'
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Alles löschen
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
